// components/StyleGuideHome.tsx
import { Box, Grid2 as Grid, Paper, Typography, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { typographySamples } from "./typography/typographySamples";
import Button from "@/components/ui/Button";
import { ButtonStyleType } from "@/types/shared/uiComponents";
import { useAppTheme } from "@/hooks/useAppTheme";

const SECTIONS_DATA = [
  {
    title: "Typography",
    description: "View heading styles, body text, and usage guidelines.",
    route: "/styleguide/typography",
    getCount: () => `${typographySamples.length} variants`,
    getLastUpdated: () => new Date().toLocaleDateString(),
  },
  {
    title: "Buttons",
    description: "Explore button variants, states, and usage.",
    route: "/styleguide/buttons",
    getCount: () => `3 types, 4 states`,
    getLastUpdated: () => new Date().toLocaleDateString(),
  },
  {
    title: "Color Palette",
    description: "See brand colors, contrast rules, and accessibility notes.",
    route: "/styleguide/colors",
    getCount: () => `12 colors`,
    getLastUpdated: () => new Date().toLocaleDateString(),
  },
  {
    title: "Spacing & Layout",
    description: "Understand grid, spacing scale, and component alignment.",
    route: "/styleguide/spacing",
    getCount: () => `5 examples`,
    getLastUpdated: () => new Date().toLocaleDateString(),
  },
  {
    title: "Custom Components",
    description: "Reusable components and their recommended patterns.",
    route: "/styleguide/components",
    getCount: () => `4 components`,
    getLastUpdated: () => new Date().toLocaleDateString(),
  },
];

export const StyleGuideHome = () => {
  const navigate = useNavigate();
  const { theme } = useAppTheme();

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h1" gutterBottom>
        Style Guide
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        A centralized reference for our product design system — intended to help developers maintain visual and UX
        consistency across the platform.
      </Typography>

      <Grid container spacing={4}>
        {SECTIONS_DATA.map((section) => (
          <Grid key={section.title} size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                height: "100%",
                borderLeft: `6px solid ${theme.palette.primary.main}`,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h3" fontSize={20} gutterBottom>
                  {section.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {section.description}
                </Typography>
              </Box>
              <Box mt={2}>
                <Box display="flex" alignItems="center" gap={1} mb={6}>
                  <Chip label={section.getCount()} size="small" color="default" sx={{ fontWeight: 500 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                    {`Last updated ${section.getLastUpdated()}`}
                  </Typography>
                </Box>
                <Button fullWidth variant={ButtonStyleType.Outlined} onClick={() => navigate(section.route)}>
                  Go to {section.title}
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
